import _ from 'lodash'
import { BranchDesignator, ValidityDeterminate, ConceptStatusValidity, Export } from '@fusion-advantage/backcompat'
import { LabsConcept, ValiditySeverity, ConceptStatusValidityInfo, AsyncResultHashConcept, ExecutableCalcCalculateFunction, ExecutableCalcCalculateOk, CalcRuntimeError, CalcType, ExecutableCalc, BMDLabInterpretation, LabActionType, cidForDirItemId, CommonResponse } from '@fusion-advantage/formlib'
import { createSelector } from '@reduxjs/toolkit'
import { FormRootState } from '../../redux/formStore'
import { selectLabRows } from '../../redux/labsSlice'
import { err, ok, Result } from 'neverthrow'

//
//
// LABS
//

export const createLabValidationCalc = (getState: () => FormRootState) => {
	const selectCalculateFn = createSelector(
		selectLabRows,
		(labs): ExecutableCalcCalculateFunction => {
			return (ctx): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
				let warningValidity: ConceptStatusValidity|null = null
				for(let { validity } of labs) {
					const severity = new ConceptStatusValidityInfo(validity).severity
					switch(severity) {
						case ValiditySeverity.Error:
							// return on first error
							return ok({ calcType: CalcType.Validation, returnValue: validity})
						case ValiditySeverity.Warning:
							// return first warning, if no error
							if(!warningValidity) {
								warningValidity = validity
							}
							break
						case ValiditySeverity.Success:
							break
						default:
							return err({
								area: 'LabValidationCalc',
								message: `Unhandled severity '${severity}'`,
							})
					}
				}
				if(warningValidity) {
					// return first warning, if no error
					return ok({ calcType: CalcType.Validation, returnValue: warningValidity })
				}
				return ok({
					calcType: CalcType.Validation,
					returnValue: {
						determinate: ValidityDeterminate.Valid,
					}
				})
			}
		}
	)
	return new ExecutableCalc({
		calcType: CalcType.Validation,
		outputCid: LabsConcept.cid,
		inputCids: [LabsConcept.cid, AsyncResultHashConcept.cid],
		selectCalculate: () => selectCalculateFn(getState())
	})
}

export const createLabFollowUpPcpValueCalc = (getState: () => FormRootState, exp: Export) => {
	const branchDesignator = exp.Form.Encounter.BranchDesignatorV2
	const selectCalculateFn = createSelector(
		selectLabRows,
		(labs): ExecutableCalcCalculateFunction => {
			return (ctx): Result<ExecutableCalcCalculateOk, CalcRuntimeError> => {
				if(branchDesignator === BranchDesignator.InHome) {
					const dir645Positive = ctx.substatusValue(cidForDirItemId(645), CommonResponse.Positive)
					if(dir645Positive) {
						return ok({ calcType: CalcType.Value, returnValue: [{ value: true }] })
					}
				}
				
				const dxOsteoporosisSelected = ctx.statusValue(cidForDirItemId(350))
				const dir234Positive = ctx.substatusValue(cidForDirItemId(234), CommonResponse.Positive)
				const dir822Positive = ctx.substatusValue(cidForDirItemId(822), CommonResponse.Positive)

				if(dxOsteoporosisSelected || dir234Positive || dir822Positive) {
					return ok({ calcType: CalcType.Value, returnValue: [{ value: true }] })
				}

				for(let lab of labs) {
					if(
						lab.model.action?.type === LabActionType.BMD &&
						(
							lab.model.action.interpretation === BMDLabInterpretation.IntermediateRisk ||
							lab.model.action.interpretation === BMDLabInterpretation.HighRisk
						)
					) {
						return ok({ calcType: CalcType.Value, returnValue: [{ value: true }] })
					}
				}

				return ok({ calcType: CalcType.Value, returnValue: [{ value: null }] })
			}
		}
	)
	const inputCids = [
		LabsConcept.cid,
		cidForDirItemId(350),
		cidForDirItemId(234),
		cidForDirItemId(822),
	]
	if(branchDesignator === BranchDesignator.InHome) {
		inputCids.push(cidForDirItemId(645))
	}
	return new ExecutableCalc({
		calcType: CalcType.Value,
		outputCid: cidForDirItemId(449),
		inputCids,
		selectCalculate: () => selectCalculateFn(getState())
	})
}